import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Building2, Mail, MapPin, Phone, Plus, Save, Search, Trash2, User, X,
} from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { HelpButton } from '../components/HelpButton';
import { CallTrees } from '../components/gsoc/CallTrees';

const cardCls = 'rounded-lg border border-line bg-surface p-4';
const selectCls = 'h-10 w-full px-3 text-sm rounded-md border border-line bg-surface text-fg';
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

const TYPE_TONE = {
  'Site Contact': 'bg-brand/10 text-brand border-brand/30',
  'Property Manager': 'bg-brand/10 text-brand border-brand/30',
  'Law Enforcement': 'bg-[hsl(var(--info-bg))] text-[hsl(var(--info))] border-[hsl(var(--info-border))]',
  'Fire / EMS': 'bg-danger-bg text-danger border-danger-border',
  'Alarm Company': 'bg-warning-bg text-fg border-warning-border',
  'Vendor / Technician': 'bg-warning-bg text-fg border-warning-border',
  'TriCorps Internal': 'bg-success-bg text-success border-success-border',
  Other: 'bg-surface-sunken text-fg-muted border-line',
};

const emptyContact = {
  name: '', company: '', title: '', contact_type: 'Site Contact',
  phone_primary: '', phone_primary_label: 'Mobile',
  phone_secondary: '', phone_secondary_label: 'Desk',
  email: '', address: '', availability: 'Unknown', call_priority: 1,
  notes: '', client_id: '', site_id: '', is_active: true,
};

const PhoneLine = ({ number, label, testid }) => number ? (
  <a href={`tel:${number.replace(/[^\d+]/g, '')}`} className="flex items-center gap-2 text-sm text-fg hover:underline"
     data-testid={testid}>
    <Phone className="w-3.5 h-3.5 text-fg-muted" aria-hidden="true" />
    <span className="font-medium">{number}</span>
    <span className="text-xs text-fg-muted">{label}</span>
  </a>
) : null;

/* ── One contact card ──────────────────────────────────────────────────── */
function ContactCard({ c, index, canEdit, canDelete, onEdit, onDelete }) {
  return (
    <div className={cardCls} data-testid={`gsoc-contact-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-fg" data-testid={`gsoc-contact-name-${index}`}>{c.name}</p>
            <Badge className={`border ${TYPE_TONE[c.contact_type] || TYPE_TONE.Other}`}>{c.contact_type}</Badge>
            {!c.is_active && <Badge className="bg-surface-sunken text-fg-muted border border-line">off</Badge>}
          </div>
          {(c.title || c.company) && (
            <p className="text-sm text-fg-muted mt-0.5">
              {[c.title, c.company].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-xs text-fg-muted mr-1">#{c.call_priority}</span>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => onEdit(c)} data-testid={`gsoc-contact-edit-${index}`}>Edit</Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => onDelete(c)} data-testid={`gsoc-contact-delete-${index}`}>
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        <PhoneLine number={c.phone_primary} label={c.phone_primary_label} testid={`gsoc-contact-phone1-${index}`} />
        <PhoneLine number={c.phone_secondary} label={c.phone_secondary_label} testid={`gsoc-contact-phone2-${index}`} />
        {c.email && (
          <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-sm text-fg hover:underline"
             data-testid={`gsoc-contact-email-${index}`}>
            <Mail className="w-3.5 h-3.5 text-fg-muted" aria-hidden="true" />{c.email}
          </a>
        )}
        {c.address && (
          <p className="flex items-start gap-2 text-sm text-fg-muted">
            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />{c.address}
          </p>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        {c.availability && c.availability !== 'Unknown' && (
          <span className="px-2 py-0.5 rounded-full bg-surface-sunken border border-line">{c.availability}</span>
        )}
        {c.client_name && (
          <span className="flex items-center gap-1">
            <Building2 className="w-3 h-3" aria-hidden="true" />{c.client_name}{c.site_name ? ` · ${c.site_name}` : ''}
          </span>
        )}
      </div>

      {c.notes && (
        <p className="mt-3 text-sm text-fg whitespace-pre-wrap border-t border-line pt-2"
           data-testid={`gsoc-contact-notes-${index}`}>{c.notes}</p>
      )}
    </div>
  );
}

/* ── Add / edit form ───────────────────────────────────────────────────── */
function ContactForm({ row, meta, clients, onCancel, onSaved }) {
  const [draft, setDraft] = useState(row);
  const [sites, setSites] = useState([]);
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  useEffect(() => {
    if (!draft.client_id) { setSites([]); return; }
    api.get('/gsoc/sites', { params: { client_id: draft.client_id } })
      .then(({ data }) => setSites(data)).catch(() => setSites([]));
  }, [draft.client_id]);

  const save = async () => {
    if (!draft.name.trim()) return toast.error('Give the contact a name');
    if (!draft.phone_primary?.trim() && !draft.email?.trim()) {
      return toast.error('Add at least a phone number or an email — otherwise there is nothing to call');
    }
    const payload = {
      ...draft,
      name: draft.name.trim(),
      call_priority: Number(draft.call_priority) || 1,
      client_id: draft.client_id || null,
      site_id: draft.site_id || null,
      company: draft.company?.trim() || null,
      title: draft.title?.trim() || null,
      phone_primary: draft.phone_primary?.trim() || null,
      phone_secondary: draft.phone_secondary?.trim() || null,
      email: draft.email?.trim() || null,
      address: draft.address?.trim() || null,
      notes: draft.notes?.trim() || null,
    };
    delete payload.client_name;
    delete payload.site_name;
    try {
      if (draft.id) await api.put(`/gsoc/contacts/${draft.id}`, payload);
      else await api.post('/gsoc/contacts', payload);
      toast.success(draft.id ? 'Contact saved' : 'Contact added');
      onSaved();
    } catch (e) { err(e, 'Could not save the contact'); }
  };

  return (
    <div className={cardCls} data-testid="gsoc-contact-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-fg">{draft.id ? 'Edit contact' : 'Add a contact'}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="gsoc-contact-form-close">
          <X className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-sm">Name</Label>
          <Input className="mt-1" value={draft.name} onChange={set('name')} data-testid="gsoc-contact-input-name" />
        </div>
        <div>
          <Label className="text-sm">Company</Label>
          <Input className="mt-1" value={draft.company || ''} onChange={set('company')} data-testid="gsoc-contact-input-company" />
        </div>
        <div>
          <Label className="text-sm">Title</Label>
          <Input className="mt-1" value={draft.title || ''} onChange={set('title')} data-testid="gsoc-contact-input-title" />
        </div>

        <div>
          <Label className="text-sm">Type</Label>
          <select className={`${selectCls} mt-1`} value={draft.contact_type} onChange={set('contact_type')}
                  data-testid="gsoc-contact-input-type">
            {(meta.contact_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm">Availability</Label>
          <select className={`${selectCls} mt-1`} value={draft.availability} onChange={set('availability')}
                  data-testid="gsoc-contact-input-availability">
            {(meta.availability || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm">Call order</Label>
          <Input type="number" min="1" max="99" className="mt-1" value={draft.call_priority}
                 onChange={set('call_priority')} data-testid="gsoc-contact-input-priority" />
        </div>

        <div className="md:col-span-2">
          <Label className="text-sm">Primary phone</Label>
          <div className="mt-1 flex gap-2">
            <Input value={draft.phone_primary || ''} onChange={set('phone_primary')}
                   placeholder="405-603-1100" data-testid="gsoc-contact-input-phone1" />
            <select className={`${selectCls} max-w-[130px]`} value={draft.phone_primary_label}
                    onChange={set('phone_primary_label')} data-testid="gsoc-contact-input-phone1-label">
              {(meta.phone_labels || []).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-sm">Email</Label>
          <Input className="mt-1" value={draft.email || ''} onChange={set('email')} data-testid="gsoc-contact-input-email" />
        </div>

        <div className="md:col-span-2">
          <Label className="text-sm">Secondary phone</Label>
          <div className="mt-1 flex gap-2">
            <Input value={draft.phone_secondary || ''} onChange={set('phone_secondary')}
                   data-testid="gsoc-contact-input-phone2" />
            <select className={`${selectCls} max-w-[130px]`} value={draft.phone_secondary_label}
                    onChange={set('phone_secondary_label')} data-testid="gsoc-contact-input-phone2-label">
              {(meta.phone_labels || []).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-sm">Address</Label>
          <Input className="mt-1" value={draft.address || ''} onChange={set('address')} data-testid="gsoc-contact-input-address" />
        </div>

        <div>
          <Label className="text-sm">Client <span className="text-fg-muted font-normal">(optional)</span></Label>
          <select className={`${selectCls} mt-1`} value={draft.client_id || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, client_id: e.target.value, site_id: '' }))}
                  data-testid="gsoc-contact-input-client">
            <option value="">Any client</option>
            {clients.map((c, ci) => <option key={c.id || `c${ci}`} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm">Site <span className="text-fg-muted font-normal">(optional)</span></Label>
          <select className={`${selectCls} mt-1`} value={draft.site_id || ''} onChange={set('site_id')}
                  disabled={!draft.client_id} data-testid="gsoc-contact-input-site">
            <option value="">{draft.client_id ? 'Any site' : 'Pick a client first'}</option>
            {sites.map((s, si) => <option key={s.id || `s${si}`} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={!!draft.is_active}
                    onCheckedChange={(v) => setDraft((d) => ({ ...d, is_active: v }))}
                    data-testid="gsoc-contact-input-active" />
            <span className="text-fg-muted">Visible to operators</span>
          </label>
        </div>
      </div>

      <div className="mt-3">
        <Label className="text-sm">Notes</Label>
        <Textarea rows={2} className="mt-1" value={draft.notes || ''} onChange={set('notes')}
                  placeholder="Gate code, ask for the night manager, do not call before 8am..."
                  data-testid="gsoc-contact-input-notes" />
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={save} data-testid="gsoc-contact-save-btn">
          <Save className="w-4 h-4 mr-1" aria-hidden="true" />Save Contact
        </Button>
      </div>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────── */
export default function GSOCContacts() {
  const perms = useEffectivePermissions();
  const isAdmin = perms.role === 'admin';
  const canEdit = isAdmin || perms.can('gsoc_passon', 'edit');
  const canDelete = isAdmin || perms.can('gsoc_passon', 'delete');

  const [tab, setTab] = useState('contacts');
  const [meta, setMeta] = useState({ contact_types: [], phone_labels: [], availability: [] });
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filters, setFilters] = useState({ q: '', contact_type: '', client_id: '' });
  const [form, setForm] = useState(null);

  const load = () => api.get('/gsoc/contacts', { params: { include_inactive: canEdit } })
    .then(({ data }) => setContacts(data)).catch(() => {});

  useEffect(() => {
    api.get('/gsoc/contacts/meta').then(({ data }) => setMeta(data)).catch(() => {});
    api.get('/gsoc/client-settings').then(({ data }) => setClients(data)).catch(() => {});
    load();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [canEdit]);

  const shown = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return contacts.filter((c) => {
      if (filters.contact_type && c.contact_type !== filters.contact_type) return false;
      if (filters.client_id && c.client_id !== filters.client_id) return false;
      if (!q) return true;
      return [c.name, c.company, c.title, c.email, c.phone_primary, c.phone_secondary, c.notes,
        c.client_name, c.site_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [contacts, filters]);

  const remove = async (c) => {
    try { await api.delete(`/gsoc/contacts/${c.id}`); toast.success('Contact deleted'); load(); }
    catch (e) { err(e, 'Could not delete the contact'); }
  };

  const tabBtn = (key, label) => (
    <button type="button" onClick={() => setTab(key)} data-testid={`gsoc-contacts-tab-${key}`}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              tab === key ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg'}`}>
      {label}
    </button>
  );

  return (
    <div className="space-y-5" data-testid="gsoc-contacts-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="page-title">Contacts &amp; Call Trees</h1>
          <p className="text-sm text-fg-muted mt-1 max-w-3xl">
            Who to call, and in what order. Search by name, company, phone number or anything in the
            notes. Call trees walk you through a specific event step by step.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton module="gsoc" label="Playbook" />
          {canEdit && tab === 'contacts' && (
            <Button onClick={() => setForm({ ...emptyContact })} data-testid="gsoc-contact-add-btn">
              <Plus className="w-4 h-4 mr-1" aria-hidden="true" />Add Contact
            </Button>
          )}
        </div>
      </div>

      <div className="inline-flex gap-1 p-1 rounded-lg bg-surface-sunken border border-line">
        {tabBtn('contacts', 'Contacts')}
        {tabBtn('trees', 'Call Trees')}
      </div>

      {tab === 'contacts' && (
        <>
          {form && (
            <ContactForm row={form} meta={meta} clients={clients}
                         onCancel={() => setForm(null)}
                         onSaved={() => { setForm(null); load(); }} />
          )}

          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" aria-hidden="true" />
              <Input className="pl-9" value={filters.q} placeholder="Search name, company, number, notes..."
                     onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                     data-testid="gsoc-contacts-search" />
            </div>
            <select className={`${selectCls} max-w-[200px]`} value={filters.contact_type}
                    onChange={(e) => setFilters((f) => ({ ...f, contact_type: e.target.value }))}
                    data-testid="gsoc-contacts-filter-type">
              <option value="">All types</option>
              {(meta.contact_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className={`${selectCls} max-w-[220px]`} value={filters.client_id}
                    onChange={(e) => setFilters((f) => ({ ...f, client_id: e.target.value }))}
                    data-testid="gsoc-contacts-filter-client">
              <option value="">All clients</option>
              {clients.map((c, ci) => <option key={c.id || `c${ci}`} value={c.id}>{c.name}</option>)}
            </select>
            <span className="text-sm text-fg-muted" data-testid="gsoc-contacts-count">
              {shown.length} {shown.length === 1 ? 'contact' : 'contacts'}
            </span>
          </div>

          {shown.length === 0 ? (
            <div className={`${cardCls} text-center py-10`} data-testid="gsoc-contacts-empty">
              <User className="w-8 h-8 mx-auto text-fg-muted mb-2" aria-hidden="true" />
              <p className="text-sm text-fg-muted">
                {contacts.length === 0
                  ? (canEdit ? 'No contacts yet — press Add Contact to start the directory.'
                             : 'No contacts have been added yet.')
                  : 'Nothing matches that search.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="gsoc-contacts-list">
              {shown.map((c, i) => (
                <ContactCard key={c.id || `row${i}`} c={c} index={i} canEdit={canEdit} canDelete={canDelete}
                             onEdit={(row) => setForm(row)} onDelete={remove} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'trees' && (
        <CallTrees contacts={contacts} canEdit={canEdit} canDelete={canDelete} />
      )}
    </div>
  );
}
